import { z } from "zod";
import { citedSiteSchema } from "./domain-source-sites.js";
import { domainSourceGroupSchema } from "./overview.js";
import { isoDateSchema as isoDate, reportFilterSchema } from "./report-filter.js";
/**
 * Every page a report's answers cited, and how that changed.
 *
 * The change is the point. A page cited 27 times is a fact; a page that went
 * from 4 to 27 is a thing that happened, and only the second is something a
 * person can act on. The comparison window is the same length as the chosen one
 * and sits immediately before it, so widening the range widens both halves.
 *
 * There is no domain category on this screen. When this was written the product
 * held no source for calling a domain "News" or "Brand", and a column of guesses
 * is worse than none (AGENTS.md §1). Off-page has since categorised cited sites,
 * with where each kind came from; the Overview's domain sources shows that,
 * labelled as such (agreed 2026-10-04). This screen has not taken it up.
 */
/** The shared range-and-engine filter, named for the screen that uses it. */
export const citationsFilterSchema = reportFilterSchema;
export const citedPageSchema = z.object({
    url: z.string(),
    domain: z.string().nullable(),
    /** Whatever the engine labelled the link. Null stays null. */
    title: z.string().nullable(),
    citations: z.number().int().min(0),
    /** The same page over the window before. Zero means it is new here. */
    previous: z.number().int().min(0),
    /** Whether the answers citing it also named the subject brand. */
    brandMentioned: z.boolean(),
    /** Whether the cited domain is one the subject owns. */
    own: z.boolean(),
    /** Brands named in the answers that cited it, most-mentioned first. */
    brands: z.array(z.object({ brandId: z.string(), brandName: z.string() })),
});
export const citationsSchema = z.object({
    reportId: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    from: isoDate,
    to: isoDate,
    /** The window the change is measured against, so the screen can name it. */
    previousFrom: isoDate,
    previousTo: isoDate,
    /** Every citation in range — the denominator for a share. */
    total: z.number().int().min(0),
    pages: z.array(citedPageSchema),
});
/**
 * One cited page, opened up.
 *
 * The list says which pages matter; this says what one page is doing — how its
 * citations moved day by day, and which questions it is being cited on.
 *
 * `prompts` is the actionable half. "Cited 27 times" is a fact about the page;
 * "cited on the four questions where your coverage is weakest" is a piece of
 * work, and the coverage travels with its denominator so a reader can tell
 * 3-of-3 from 30-of-40.
 */
export const citationPromptSchema = z.object({
    promptId: z.string(),
    text: z.string(),
    /** Citations of this page on this question, in range. */
    citations: z.number().int().min(0),
    /** Every answer to the question — the denominator coverage is taken against. */
    responses: z.number().int().min(0),
    naming: z.number().int().min(0),
    /** Which engines cited it here. Distinct, in the order the database returns. */
    engines: z.array(z.string()),
    /** The citations on this question split by engine, most first. */
    byEngine: z.array(z.object({ engine: z.string(), citations: z.number().int().min(0) })),
});
export const citationDetailSchema = z.object({
    url: z.string(),
    domain: z.string().nullable(),
    title: z.string().nullable(),
    citations: z.number().int().min(0),
    brandMentioned: z.boolean(),
    own: z.boolean(),
    brandName: z.string(),
    brands: z.array(z.object({ brandId: z.string(), brandName: z.string() })),
    /**
     * Citations per day.
     *
     * Built from the days that were *captured*, so a day with captures and no
     * citation of this page is a real zero rather than a hole in the line. The
     * difference matters: a gap says we did not look.
     */
    days: z.array(z.object({ day: isoDate, citations: z.number().int().min(0) })),
    prompts: z.array(citationPromptSchema),
});
/**
 * One site, every page of it together: "Instagram was cited 20 times — on these
 * questions, by these engines, through these pages". Asked for 2026-10-03, to
 * show a client where a channel is being cited.
 */
export const citationSiteSchema = z.object({
    domain: z.string(),
    brandName: z.string(),
    /** Every citation of the site in range, the sum of its pages. */
    citations: z.number().int().min(0),
    own: z.boolean(),
    pages: z.array(z.object({
        url: z.string(),
        title: z.string().nullable(),
        citations: z.number().int().min(0),
    })),
    prompts: z.array(citationPromptSchema),
});
/**
 * Sources, a page at a time (DESIGN.md §9, "Sources"). Rust only.
 *
 * The screen used to download every cited page in range (`citationsSchema`)
 * and every cited site (`citedSitesSchema`), then add, search, sort and slice
 * them in the browser: two thousand rows for one brand over a month. A list
 * that can pass a hundred rows is paged by the server (AGENTS.md §15a), so
 * this answers with the summary cards' figures and one page of one table,
 * both worked out from the same read, so a card and the table under it
 * cannot disagree. The old two reads stay for a browser still running the old
 * screen.
 */
/** Rows per page of a Sources table. */
export const SOURCES_PAGE = 50;
/** The tabs with a table; Visits from AI is Google Analytics' own read. */
export const SOURCES_TABS = ["sites", "pages", "yours"];
/** The Pages tab's lists: every page, or those that rose or fell. */
export const PAGE_LISTS = ["cited", "gaining", "slipping"];
/** The columns a table sorts by. `name` is a site's domain, or a page's. */
export const SOURCES_SORTS = ["citations", "change", "name"];
export const SORT_ORDERS = ["asc", "desc"];
export const sourcesQuerySchema = citationsFilterSchema.extend({
    tab: z.enum(SOURCES_TABS).optional(),
    /** Words, or a pasted link matched as its page (`pageKey`). */
    q: z.string().max(2048).optional(),
    /** One part of the ring, on the Sites tab. */
    kind: domainSourceGroupSchema.optional(),
    list: z.enum(PAGE_LISTS).optional(),
    sort: z.enum(SOURCES_SORTS).optional(),
    /** Without it a name sorts A to Z and a number most first. */
    order: z.enum(SORT_ORDERS).optional(),
    offset: z.coerce.number().int().min(0).max(100_000).default(0),
});
const count = z.number().int().min(0);
/**
 * A site: its pages added together, so its number is always the sum of pages
 * the reader can open. `placed` is null for a site cited only in the days
 * before.
 */
export const sourceSiteSchema = z.object({
    domain: z.string(),
    citations: count,
    previous: count,
    /** Pages cited in this range. */
    pages: count,
    own: z.boolean(),
    /** Whether an answer citing it in this range named the report's brand. */
    namesYou: z.boolean(),
    placed: citedSiteSchema.nullable(),
});
export const sourcesSummarySchema = z.object({
    /** Every citation in range, with a site or not. */
    total: count,
    /** Every citation in the days before. Zero means nothing has a change. */
    previousTotal: count,
    /** How many sites were cited in range. */
    sites: count,
    /** Every part in the ring's order, zeros included. */
    parts: z.array(z.object({ group: domainSourceGroupSchema, citations: count, sites: count })),
    /** The report's own site: now, its pages cited now, and before. */
    own: z.object({ citations: count, pages: count, previous: count }),
});
/** The order the rows are in, said back so the head shows it. */
const sortSaidSchema = z.object({
    key: z.enum(SOURCES_SORTS),
    order: z.enum(SORT_ORDERS),
});
/** `matched` counts every row the search and the part or list kept. */
export const sourcesTableSchema = z.discriminatedUnion("tab", [
    z.object({
        tab: z.literal("sites"),
        offset: count,
        matched: count,
        sort: sortSaidSchema,
        /** The most citations of any site kept: what the share bars scale to. */
        most: count,
        rows: z.array(sourceSiteSchema),
    }),
    z.object({
        tab: z.literal("pages"),
        offset: count,
        matched: count,
        sort: sortSaidSchema,
        /** True when the search was a link to a page, matched as one. */
        isLink: z.boolean(),
        lists: z.object({ cited: count, gaining: count, slipping: count }),
        rows: z.array(citedPageSchema),
    }),
    z.object({
        tab: z.literal("yours"),
        offset: count,
        matched: count,
        sort: sortSaidSchema,
        isLink: z.boolean(),
        rows: z.array(citedPageSchema),
    }),
]);
export const sourcesSchema = z.object({
    reportId: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    from: isoDate,
    to: isoDate,
    previousFrom: isoDate,
    previousTo: isoDate,
    summary: sourcesSummarySchema,
    table: sourcesTableSchema,
});
//# sourceMappingURL=citations.js.map